import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  DatePicker,
  Select,
  Row,
  Col,
  Space,
  Typography,
  Tooltip,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ShoppingOutlined, FileExcelOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import ProductImagesFormField from '../../../components/productImagesForField/ProductImagesFormField';
import '../../order/edit/OrderEdit.css';
import './CreateBulkOrder.css';

const MODE_OF_PAYMENT_OPTIONS = [
  { value: 'PayPal', label: 'PayPal' },
  { value: 'Razorpay', label: 'Razorpay' },
  { value: 'PayGlocal', label: 'PayGlocal' },
  { value: 'Bank Transfer', label: 'Bank Transfer' },
  { value: 'Others', label: 'Others' },
];

const ORDER_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'inprogress', label: 'In progress' },
  { value: 'ready', label: 'Ready' },
  { value: 'dispatch', label: 'Dispatch' },
];

/** Max lengths for text fields (characters) */
const LEN = {
  customerName: 100,
  concernedPerson: 100,
  bulkOrderNumber: 50,
  itemName: 150,
  itemDescription: 500,
  remark: 200,
};

const MAX_IMAGES_PER_ITEM = 5;

/** Max values for numeric line-item fields */
const MAX_QTY = 999999;
const MAX_PRICE = 999999999;

const MAX_LINE_ITEMS = 30;
export default function CreateBulkOrder() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const excelInputRef = React.useRef(null);

  const handleImportExcel = () => {
    excelInputRef.current?.click();
  };

  const handleExcelFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

        if (!rows || rows.length < 2) {
          dispatch(setMessage({ message: 'Excel file has no data rows', type: 'warning' }));
          return;
        }

        const validStatuses = ORDER_STATUS_OPTIONS.map((o) => o.value);
        const parseExcelDate = (val) => {
          if (val == null || val === '') return null;
          if (typeof val === 'string') return dayjs(val).isValid() ? dayjs(val) : null;
          if (typeof val === 'number') {
            const d = new Date((val - 25569) * 86400 * 1000);
            return isNaN(d.getTime()) ? null : dayjs(d);
          }
          return null;
        };

        const orderList = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!Array.isArray(row)) continue;
          const itemName = String(row[0] ?? '').trim();
          const quantity = Number(row[1]);
          const pricePerPiece = Number(row[2]);
          const deliveryDate = parseExcelDate(row[3]);
          let orderStatus = String(row[4] ?? '').trim().toLowerCase();
          if (!validStatuses.includes(orderStatus)) orderStatus = 'open';
          const itemDescription = String(row[5] ?? '').trim();
          const remark = String(row[6] ?? '').trim();

          if (!itemName && quantity === 0 && !pricePerPiece && !itemDescription && !remark) continue;

          orderList.push({
            itemName: itemName || '',
            quantity: Number.isNaN(quantity) ? 0 : quantity,
            pricePerPiece: Number.isNaN(pricePerPiece) ? 0 : pricePerPiece,
            deliveryDate: deliveryDate || null,
            orderStatus,
            itemDescription: itemDescription || '',
            remark: remark || '',
            photoOrFile: [],
          });
        }

        if (orderList.length === 0) {
          dispatch(setMessage({ message: 'No valid line items found in Excel', type: 'warning' }));
          return;
        }

        if (orderList.length > MAX_LINE_ITEMS) {
          orderList.splice(MAX_LINE_ITEMS);
          dispatch(setMessage({ message: `Only first ${MAX_LINE_ITEMS} line items imported (max ${MAX_LINE_ITEMS} allowed)`, type: 'warning' }));
        }

        form.setFieldValue('orderList', orderList);
        dispatch(setMessage({ message: `${orderList.length} line item(s) imported`, type: 'success' }));
      } catch (err) {
        dispatch(setMessage({ message: 'Failed to read Excel file', type: 'error' }));
      }
    };
    reader.onerror = () => {
      dispatch(setMessage({ message: 'Failed to read file', type: 'error' }));
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownloadSample = () => {
    const headers = [
      'Item name',
      'Quantity',
      'Price per piece',
      'Delivery date (YYYY-MM-DD)',
      'Status (open|inprogress|ready|dispatch)',
      'Description',
      'Remark',
    ];
    const sampleRows = [
      ['Sample product A', 10, 25.5, '2026-03-01', 'open', 'Short description', 'Optional note'],
      ['Sample product B', 5, 100, '2026-03-15', 'inprogress', '', ''],
    ];
    const data = [headers, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [
      { wch: 25 },
      { wch: 12 },
      { wch: 14 },
      { wch: 22 },
      { wch: 38 },
      { wch: 25 },
      { wch: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Line items');
    XLSX.writeFile(wb, 'sample-bulk-order.xlsx');
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const rawList = values.orderList || [];
      if (rawList.length > MAX_LINE_ITEMS) {
        dispatch(setMessage({ message: `Maximum ${MAX_LINE_ITEMS} line items allowed`, type: 'warning' }));
        return;
      }
      setSubmitLoading(true);
      const orderList = (values.orderList || []).map((item) => ({
        orderStatus: ORDER_STATUS_OPTIONS.some((o) => o.value === item.orderStatus) ? item.orderStatus : 'open',
        photoOrFile: Array.isArray(item.photoOrFile) ? item.photoOrFile : [],
        itemName: item.itemName?.trim() ?? '',
        itemDescription: item.itemDescription?.trim() ?? '',
        quantity: Number(item.quantity) || 0,
        pricePerPiece: Number(item.pricePerPiece) || 0,
        deliveryDate: item.deliveryDate ? dayjs(item.deliveryDate).toISOString() : null,
        remark: item.remark?.trim() ?? '',
      }));
      const payload = {
        customerName: values.customerName?.trim() ?? '',
        orderAmount: Number(values.orderAmount) ?? 0,
        modeOfPayment: values.modeOfPayment,
        concernedPerson: values.concernedPerson?.trim() ?? '',
        orderDate: values.orderDate ? dayjs(values.orderDate).toISOString() : null,
        targetDate: values.targetDate ? dayjs(values.targetDate).toISOString() : null,
        bulkOrderNumber: values.bulkOrderNumber?.trim() ?? '',
        orderList,
      };
      await axios.post('/bulk-orders', payload);
      dispatch(setMessage({ message: 'Bulk order created successfully', type: 'success' }));
      navigate('/bulk-orders');
    } catch (err) {
      if (err.errorFields) return;
      const msg = err.response?.data?.error || 'Create bulk order failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="order-edit-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="order-edit-header">
          <div className="order-edit-header-left">
            <Typography.Title level={3} style={{ margin: 0 }}>
              New bulk order
            </Typography.Title>
          </div>
          <Space>
            <Button
              type="primary"
              size="large"
              loading={submitLoading}
              onClick={() => form.submit()}
              className="app-btn-primary"
            >
              Create bulk order
            </Button>
            <Button size="large" onClick={() => navigate('/bulk-orders')} className="app-btn-cancel">
              Cancel
            </Button>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="order-edit-form"
          initialValues={{
            customerName: '',
            orderAmount: 0,
            modeOfPayment: undefined,
            concernedPerson: '',
            orderDate: null,
            targetDate: null,
            bulkOrderNumber: '',
            orderList: [{}],
          }}
        >
          <Card title="Order details" className="order-edit-card">
            <div className="order-edit-body">
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="customerName"
                    label="Customer name"
                    rules={[
                      { required: true, message: 'Customer name is required' },
                      { max: LEN.customerName, message: `Customer name must be at most ${LEN.customerName} characters` },
                    ]}
                  >
                    <Input placeholder="Customer name" maxLength={LEN.customerName} showCount />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="orderAmount" label="Order amount" rules={[{ required: true, message: 'Order amount is required' }]}>
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" onKeyDown={(e) => { if (!/[0-9.]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key)) e.preventDefault(); }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="modeOfPayment" label="Mode of payment" rules={[{ required: true, message: 'Please select mode of payment' }]}>
                    <Select placeholder="Select mode" options={MODE_OF_PAYMENT_OPTIONS} allowClear={false} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="concernedPerson"
                    label="Concerned person for order"
                    rules={[{ max: LEN.concernedPerson, message: `Concerned person must be at most ${LEN.concernedPerson} characters` }]}
                  >
                    <Input placeholder="Concerned person" maxLength={LEN.concernedPerson} showCount />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="bulkOrderNumber"
                    label="Bulk order number"
                    help="Leave empty to auto-generate (BO-DDMMYYYY-NNN)"
                    rules={[{ max: LEN.bulkOrderNumber, message: `Bulk order number must be at most ${LEN.bulkOrderNumber} characters` }]}
                  >
                    <Input placeholder="Auto-generated if empty" maxLength={LEN.bulkOrderNumber} showCount />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="orderDate" label="Order date" rules={[{ required: true, message: 'Order date is required' }]}>
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="Select date" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="targetDate" label="Target date">
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="Select date" />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Card>

          <Card className="order-edit-card bulk-order-list-card" style={{ marginTop: 16 }}>
            <div className="bulk-order-list-header">
              <div className="bulk-order-list-header-top">
                <div>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    Line items
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    Add products or services for this bulk order
                  </Typography.Text>
                </div>
                <input
                  ref={excelInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleExcelFileChange}
                />
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={handleImportExcel}
                  className="bulk-order-import-excel-btn"
                >
                  Import Excel
                </Button>
                <Button
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadSample}
                  className="bulk-order-download-sample-btn"
                >
                  Download sample file
                </Button>
              </div>
            </div>
            <Form.List name="orderList">
              {(fields, { add, remove }) => (
                <div className="bulk-order-list-body">
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className="bulk-order-line-card">
                      <div className="bulk-order-line-card-header">
                        <Space align="center" size={12}>
                          <span className="bulk-order-line-badge">
                            <ShoppingOutlined className="bulk-order-line-badge-icon" />
                            Item {index + 1}
                          </span>
                        </Space>
                        <Tooltip title="Remove line item">
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            className="bulk-order-line-remove"
                            aria-label="Remove line"
                          />
                        </Tooltip>
                      </div>
                      <div className="bulk-order-line-card-content">
                        <Row gutter={[16, 14]} className="bulk-order-line-fields">
                          <Col span={24} className="bulk-order-field-row">
                            <Form.Item
                              {...restField}
                              name={[name, 'photoOrFile']}
                              label="Photos"
                              className="bulk-order-field"
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (!value || !Array.isArray(value) || value.length <= MAX_IMAGES_PER_ITEM) return Promise.resolve();
                                    return Promise.reject(new Error(`Maximum ${MAX_IMAGES_PER_ITEM} images allowed per item`));
                                  },
                                },
                              ]}
                            >
                              <ProductImagesFormField addButtonText="Add photo" hideHint className="bulk-order-upload" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={8} md={8} className="bulk-order-field-row">
                            <Form.Item
                              {...restField}
                              name={[name, 'itemName']}
                              label="Item name"
                              className="bulk-order-field"
                              rules={[
                                { required: true, message: 'Item name is required' },
                                { max: LEN.itemName, message: `Item name must be at most ${LEN.itemName} characters` },
                              ]}
                            >
                              <Input placeholder="Item name" maxLength={LEN.itemName} showCount />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4} md={3} className="bulk-order-field-row">
                            <Form.Item
                              {...restField}
                              name={[name, 'quantity']}
                              label="Qty"
                              className="bulk-order-field"
                              rules={[
                                { required: true, message: 'Qty is required' },
                                { type: 'number', min: 0, message: 'Qty must be at least 0' },
                                { type: 'number', max: MAX_QTY, message: `Qty must be at most ${MAX_QTY.toLocaleString()}` },
                              ]}
                            >
                              <InputNumber min={0} max={MAX_QTY} placeholder="0" style={{ width: '100%' }} onKeyDown={(e) => { if (!/[0-9.]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key)) e.preventDefault(); }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4} md={3} className="bulk-order-field-row">
                            <Form.Item
                              {...restField}
                              name={[name, 'pricePerPiece']}
                              label="Price"
                              className="bulk-order-field"
                              rules={[
                                { required: true, message: 'Price is required' },
                                { type: 'number', min: 0, message: 'Price must be at least 0' },
                                { type: 'number', max: MAX_PRICE, message: `Price must be at most ${MAX_PRICE.toLocaleString()}` },
                              ]}
                            >
                              <InputNumber min={0} max={MAX_PRICE} placeholder="0" style={{ width: '100%' }} onKeyDown={(e) => { if (!/[0-9.]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key)) e.preventDefault(); }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4} md={6} className="bulk-order-field-row">
                            <Form.Item {...restField} name={[name, 'deliveryDate']} label="Delivery date" className="bulk-order-field">
                              <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="Select" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4} md={4} className="bulk-order-field-row">
                            <Form.Item {...restField} name={[name, 'orderStatus']} label="Status" initialValue="open" className="bulk-order-field">
                              <Select placeholder="Status" options={ORDER_STATUS_OPTIONS} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="bulk-order-field-row bulk-order-desc-remark-start">
                            <Form.Item {...restField} name={[name, 'itemDescription']} label="Description" className="bulk-order-field" rules={[{ max: LEN.itemDescription, message: `Description must be at most ${LEN.itemDescription} characters` }]}>
                              <Input.TextArea rows={2} placeholder="Short description" maxLength={LEN.itemDescription} showCount />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12} className="bulk-order-field-row bulk-order-desc-remark-start">
                            <Form.Item {...restField} name={[name, 'remark']} label="Remark" className="bulk-order-field" rules={[{ max: LEN.remark, message: `Remark must be at most ${LEN.remark} characters` }]}>
                              <Input.TextArea rows={2} placeholder="Optional note" maxLength={LEN.remark} showCount />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => fields.length < MAX_LINE_ITEMS && add()}
                    block
                    icon={<PlusOutlined />}
                    className="bulk-order-add-btn"
                    size="large"
                    disabled={fields.length >= MAX_LINE_ITEMS}
                  >
                    {fields.length >= MAX_LINE_ITEMS ? `Maximum ${MAX_LINE_ITEMS} line items reached` : 'Add line item'}
                  </Button>
                </div>
              )}
            </Form.List>
          </Card>
        </Form>
      </Space>
    </div>
  );
}
